import { Col, Input, InputRef, Switch, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addCardProps } from '../../actions';
import { IState } from '../../graphql/types';
import { formInputLength, handleInputChange } from '../utils';
import { CardFormField } from './CardFormField';

import styles from './CardForm.module.css';

const emptyList: string[] = [];
const startRows = 5;
const maxRows = 10;
for (let i = 0; i < startRows; i++) {
  emptyList[i] = '';
}

const ListForm: React.FC<{ validationFailed: boolean; saving: boolean }> = ({
  validationFailed,
  saving,
}) => {
  const dispatch = useDispatch();
  const title = useSelector<IState, string | undefined>(
    state => state.cardProps?.title as string | undefined
  );
  const numbered = useSelector<IState, boolean | undefined>(
    state => state.cardProps?.numbered as boolean | undefined
  );
  const listItems = useSelector<IState, string[]>(
    state => (state.cardProps?.listItems || emptyList) as string[],
    () => false
  );
  const [indexOfFocusItem, setIndexOfFocusItem] = useState<number>(0);
  const ref = React.createRef<InputRef>();

  useEffect(() => {
    if (listItems.length < startRows) {
      setIndexOfFocusItem(listItems.length);
      const tmpEmptyList: string[] = [];
      for (let i = listItems.length; i < startRows; i++) {
        tmpEmptyList.push('');
      }
      dispatch(addCardProps('listItems', [...listItems, ...tmpEmptyList]));
    }
  }, [listItems, dispatch]);

  const updateItem = React.useCallback(
    index => e => {
      const tmpListItems = listItems.map((item, i) =>
        i === index ? e.target.value : item
      );
      if (index >= startRows) {
        for (let i = tmpListItems.length - 1; i >= startRows; i--) {
          if (!tmpListItems[i]) {
            tmpListItems.splice(i, 1);
          } else if (tmpListItems[i]) {
            break;
          }
        }
      }
      if (
        index >= startRows &&
        index < maxRows - 1 &&
        listItems.length !== tmpListItems.length
      ) {
        ref!.current!.focus();
      }
      dispatch(addCardProps('listItems', tmpListItems));
    },
    [dispatch, listItems, ref]
  );

  const scrollToNewInput = useCallback(() => {
    setTimeout(() => {
      document.getElementById('lastInput')?.scrollIntoView();
    }, 100);
  }, []);

  const createItem = React.useCallback(
    e => {
      dispatch(addCardProps('listItems', [...listItems, e.target.value]));
      scrollToNewInput();
    },
    [scrollToNewInput, dispatch, listItems]
  );

  const updateNumbered = React.useCallback(
    e => dispatch(addCardProps('numbered', e)),
    [dispatch]
  );

  return (
    <div className={styles.formWrapper}>
      <Col span={24}>
        <CardFormField
          name="listTitle"
          fieldLabel="Title (optional)"
          defaultValue={title}
          customValidator={e => formInputLength(e.target.value, 100)}
          customRules={[
            { max: 100, message: 'Limit of 100 characters exceeded' },
          ]}
          required={false}
        >
          <Input
            autoFocus
            className="modal-form-input"
            onChange={handleInputChange(value =>
              dispatch(addCardProps('title', value))
            )}
            disabled={saving}
            placeholder={'Title'}
          />
        </CardFormField>
      </Col>
      <Col span={24}>
        <span>Bullets</span>
        <Switch
          className={`${styles.listSwitch} ${
            numbered ? 'switchEnabled' : 'switchDisabled'
          }`}
          size="small"
          onChange={e => updateNumbered(e)}
          defaultChecked={numbered}
        />
        <span>Numbers</span>
      </Col>
      <Col span={24}>
        <Typography className={styles.formText}>Items</Typography>
        <div>
          {listItems.map((item, index) => (
            <Input
              id={`listItem${index}`}
              autoFocus={
                (listItems.length - 1 === index && index >= startRows) ||
                indexOfFocusItem === index
              }
              key={index}
              className={`modal-form-input list-item-input ${
                validationFailed && index === 0 ? styles.notValidInput : ''
              }`}
              value={item}
              onChange={e => {
                updateItem(index)(e);
              }}
              maxLength={100}
              disabled={saving}
            />
          ))}
          {listItems[listItems.length - 1] &&
            listItems.length >= startRows &&
            listItems.length < maxRows && (
              <Input
                ref={ref}
                id="lastInput"
                autoFocus={listItems.length !== 5}
                className={'modal-form-input list-item-input'}
                value={''}
                onChange={e => createItem(e)}
                maxLength={100}
                disabled={saving}
              />
            )}
        </div>
      </Col>
    </div>
  );
};

export default ListForm;
